// Shared quick add popup.
//
// Used by the PDP "Suggested for you" carousel and by product cards on
// collection pages. Everything is delegated off the document so triggers and
// popups added after load (Swiper loop clones, collection Load More) work
// without re-binding.
//
// Markup contract:
//   - trigger:   .quick-add-popup-trigger inside an element with [data-product-id]
//   - direct add: .quick-add-direct-add inside .quick-add-button[data-default-variant-id]
//   - popup:     .quick-add-popup[data-popup-id="<product id>"] inside
//                .quick-add-popup-container

const POPUP_SELECTOR = ".quick-add-popup";
const CONTAINER_SELECTOR = ".quick-add-popup-container";

const addToCart = async (variantId, quantity = 1, closePopups = false) => {
  if (!variantId) {
    console.log("quick add: no variant id passed - cannot add to cart.");
    return;
  }

  const cart = window.Carbon.Cart;
  const response = await cart.addItemToCart(variantId, quantity);
  if (!response) return;

  if (!cart.useCartAside) {
    window.location = "/cart";
    return;
  }

  const $cartAsideBackground = document.querySelector(".cart-aside__background");
  const $cartAside = $cartAsideBackground.querySelector("aside.cart-aside");

  document.querySelector("body").classList.add("overflow-y-hidden");
  $cartAsideBackground.classList.remove("pointer-events-none");
  $cartAsideBackground.classList.add("bg-black", "bg-opacity-40");
  $cartAside.classList.remove("translate-x-full", "ponter-events-none", "hidden");

  if (closePopups) closeAllPopups();
};

const closeAllPopups = () => {
  document.querySelectorAll(CONTAINER_SELECTOR).forEach(($container) => {
    $container.classList.remove("active");
  });
  document.querySelectorAll(POPUP_SELECTOR).forEach(($popup) => {
    $popup.classList.remove("active");
  });
};

const closeFlyout = ($flyout) => {
  $flyout?.classList.add("opacity-0", "pointer-events-none");
};

// The same product can have a popup in more than one section (e.g. PDP
// carousel + cart drawer), so prefer the one in the trigger's own section.
const openPopup = (productId, $scope = document) => {
  const selector = `${POPUP_SELECTOR}[data-popup-id="${productId}"]`;
  const $popup =
    $scope.querySelector(selector) ?? document.querySelector(selector);

  if (!$popup) {
    console.log(`quick add: no popup found for product ${productId}`);
    return;
  }

  closeAllPopups();
  $popup.classList.add("active");
  $popup.closest(CONTAINER_SELECTOR)?.classList.add("active");
};

// Push the master select's chosen variant back out to the visual option
// buttons / dropdowns so they reflect what is actually selected.
const syncOptionsToSelect = ($variantMasterSelect, $optionGroups) => {
  const selectedOption =
    $variantMasterSelect.options[$variantMasterSelect.selectedIndex];

  $optionGroups.forEach(($optionGroup) => {
    const position = parseInt($optionGroup.dataset.optionPosition);
    const targetValue = selectedOption.dataset[`option${position}`];

    const $buttons = $optionGroup.querySelectorAll(".options__button");
    $buttons.forEach(($button) => {
      $button.classList.toggle(
        "options__button--active",
        $button.dataset.optionHandle === targetValue
      );
    });

    const $select = $optionGroup.querySelector(".options__select");
    if ($select) $select.value = targetValue;
  });
};

// Work out which variant matches the current combination of options.
const findVariantOption = (
  $variantMasterSelect,
  $optionGroups,
  changedPosition,
  changedValue
) => {
  const currentOptions = {};

  $optionGroups.forEach(($optionGroup) => {
    const position = parseInt($optionGroup.dataset.optionPosition);
    const $activeButton = $optionGroup.querySelector(".options__button--active");
    const $select = $optionGroup.querySelector(".options__select");

    if ($activeButton) {
      currentOptions[position] = $activeButton.dataset.optionHandle;
    } else if ($select) {
      currentOptions[position] = $select.value;
    }
  });

  currentOptions[changedPosition] = changedValue;

  const options = Array.from($variantMasterSelect.options);

  const exactMatch = options.find((opt) =>
    Object.entries(currentOptions).every(
      ([pos, val]) => opt.dataset[`option${pos}`] === val
    )
  );
  if (exactMatch) return exactMatch;

  // No exact match - keep the option the user just changed, relax the rest.
  return (
    options.find(
      (opt) => opt.dataset[`option${changedPosition}`] === changedValue
    ) ?? options[0]
  );
};

const updatePrice = ($popup) => {
  const $variantMasterSelect = $popup.querySelector(
    ".quick-add-popup__variant-select"
  );
  const $quantityValue = $popup.querySelector(".product-template__quantity-value");
  const $priceElements = $popup.querySelectorAll(".quick-add-popup__price");

  let price = $popup.dataset.firstVariantPrice;
  if ($variantMasterSelect) {
    const selectedOption =
      $variantMasterSelect.options[$variantMasterSelect.selectedIndex];
    price = parseInt(selectedOption.dataset.price);
  }

  const quantity = $quantityValue ? parseInt($quantityValue.textContent) : 1;
  const formatted = window.Carbon.Money.formatMoney(price * quantity);

  $priceElements.forEach(($price) => {
    $price.textContent = formatted;
  });
};

const applyVariantChange = ($popup, position, value) => {
  const $variantMasterSelect = $popup.querySelector(
    ".quick-add-popup__variant-select"
  );
  if (!$variantMasterSelect) return;

  const $optionGroups = $popup.querySelectorAll(".product-template__variant");
  const match = findVariantOption(
    $variantMasterSelect,
    $optionGroups,
    position,
    value
  );
  if (!match) return;

  $variantMasterSelect.value = match.value;
  syncOptionsToSelect($variantMasterSelect, $optionGroups);
  updatePrice($popup);
};

export const quickAddPopup = () => {
  document.addEventListener("click", (event) => {
    const $target = event.target;

    // Open - the trigger sits inside whatever carries the product id
    // (a swiper slide, a product card...)
    const $trigger = $target.closest(".quick-add-popup-trigger");
    if ($trigger) {
      event.preventDefault();
      const productId = $trigger.closest("[data-product-id]")?.dataset.productId;
      if (productId) {
        openPopup(productId, $trigger.closest(".shopify-section") ?? document);
      } else {
        console.log("quick add: trigger has no [data-product-id] ancestor");
      }
      return;
    }

    // Add straight to cart, no popup - single variant products
    const $directAdd = $target.closest(".quick-add-direct-add");
    if ($directAdd) {
      event.preventDefault();
      const $button = $directAdd.closest(".quick-add-button");
      if ($button) {
        addToCart($button.dataset.defaultVariantId);
      } else {
        console.log("quick add: direct add has no .quick-add-button wrapper");
      }
      return;
    }

    // Close
    if (
      $target.closest(".quick-add-popup__close") ||
      $target.closest(".quick-add-popup-container-bg")
    ) {
      event.preventDefault();
      closeAllPopups();
      return;
    }

    // Flyout close - the PDP binds its own, this covers the collection page
    if ($target.closest(".info__grind-flyout .top__close")) {
      event.preventDefault();
      closeFlyout(document.querySelector(".info__grind-flyout"));
      return;
    }
    const $flyout = $target.closest(".info__grind-flyout");
    if ($flyout && $target === $flyout) {
      event.preventDefault();
      closeFlyout($flyout);
      return;
    }

    const $popup = $target.closest(POPUP_SELECTOR);
    if (!$popup) return;

    // Grind Guide - only from within a popup, the PDP has its own binding
    if ($target.closest(".option__grind-button")) {
      event.preventDefault();
      const $grindFlyout = document.querySelector(".info__grind-flyout");
      if ($grindFlyout) {
        $grindFlyout.classList.remove("opacity-0", "pointer-events-none");
      } else {
        console.log("quick add: no grind flyout on this page");
      }
      return;
    }

    // Variant option buttons
    const $optionButton = $target.closest(".options__button");
    if ($optionButton) {
      event.preventDefault();
      const $optionGroup = $optionButton.closest(".product-template__variant");
      $optionGroup
        .querySelectorAll(".options__button")
        .forEach(($button) =>
          $button.classList.remove("options__button--active")
        );
      $optionButton.classList.add("options__button--active");

      applyVariantChange(
        $popup,
        parseInt($optionGroup.dataset.optionPosition),
        $optionButton.dataset.optionHandle
      );
      return;
    }

    // Quantity
    const $quantityValue = $popup.querySelector(
      ".product-template__quantity-value"
    );
    if ($target.closest(".product-template__quantity-decrease")) {
      event.preventDefault();
      const current = parseInt($quantityValue.textContent);
      if (current > 1) {
        $quantityValue.textContent = current - 1;
        updatePrice($popup);
      }
      return;
    }
    if ($target.closest(".product-template__quantity-increase")) {
      event.preventDefault();
      $quantityValue.textContent = parseInt($quantityValue.textContent) + 1;
      updatePrice($popup);
      return;
    }

    // Add to cart from within the popup
    if ($target.closest(".quick-add-popup__add-to-cart")) {
      event.preventDefault();
      const $variantMasterSelect = $popup.querySelector(
        ".quick-add-popup__variant-select"
      );
      const variantId = $variantMasterSelect
        ? $variantMasterSelect.value
        : $popup.dataset.defaultVariantId;
      const quantity = $quantityValue ? parseInt($quantityValue.textContent) : 1;

      if (variantId) {
        addToCart(variantId, quantity, true);
      } else {
        console.log("quick add: could not determine variant id for add to cart");
      }
    }
  });

  // Master select can be changed directly on mobile
  document.addEventListener("change", (event) => {
    const $popup = event.target.closest(POPUP_SELECTOR);
    if (!$popup) return;

    if (event.target.matches(".quick-add-popup__variant-select")) {
      syncOptionsToSelect(
        event.target,
        $popup.querySelectorAll(".product-template__variant")
      );
      updatePrice($popup);
      return;
    }

    if (event.target.matches(".options__select")) {
      const $optionGroup = event.target.closest(".product-template__variant");
      applyVariantChange(
        $popup,
        parseInt($optionGroup.dataset.optionPosition),
        event.target.value
      );
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeAllPopups();
  });
};

export default quickAddPopup;
